// CompanySettingsPage.jsx
// Admin-only page for business reference data (registered/trading address,
// company number, VAT number, bank details, insurance details, standard
// terms) - stored as simple key/value pairs on their own sheet tab
// (CompanySettings), editable here with no redeploy needed. Deliberately
// holds no API keys/credentials/service secrets - those stay in Vercel env
// vars; this page is reference data only.

import React, { useState, useEffect } from "react";
import { useUser } from "@clerk/react";
import { fetchCompanySettings, saveCompanySettings, COMPANY_SETTINGS_FIELDS, fetchDocuments, addDocument, deleteDocument, fetchSuppliers, saveSupplier, deleteSupplier, SUPPLIER_PRICE_STALE_DAYS, daysSince } from "./quoteLogger";
import { C } from "./QuoteLog";

const inputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${C.border}`,
  fontSize: 13, outline: "none", background: "#fff", color: C.text,
  fontFamily: "'IBM Plex Sans', sans-serif", boxSizing: "border-box",
};
const labelStyle = {
  fontSize: 11, fontWeight: 600, color: "#4a6483", textTransform: "uppercase",
  letterSpacing: "0.05em", display: "block", marginBottom: 6,
};

function Field({ field, value, onChange }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>{field.label}</label>
        {field.type === "url" && value && (
          <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: C.brand, fontWeight: 600, textDecoration: "none" }}>
            Open ↗
          </a>
        )}
      </div>
      {field.type === "textarea" ? (
        <textarea
          style={{ ...inputStyle, minHeight: 72, resize: "vertical" }}
          value={value}
          onChange={e => onChange(field.key, e.target.value)}
        />
      ) : (
        <input
          style={inputStyle}
          type={field.type === "number" ? "number" : field.type === "url" ? "url" : "text"}
          value={value}
          placeholder={field.type === "url" ? "https://…" : undefined}
          onChange={e => onChange(field.key, e.target.value)}
        />
      )}
    </div>
  );
}

function LabeledInput({ label, value, onChange, type, placeholder, readOnly }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        style={{ ...inputStyle, ...(readOnly ? { background: "#F4F6FA", color: C.textMuted } : {}) }}
        type={type || "text"}
        value={value || ""}
        placeholder={placeholder}
        readOnly={readOnly}
        onChange={onChange ? e => onChange(e.target.value) : undefined}
      />
    </div>
  );
}

// Editable list of small records (delivered tiers, extras): one row per
// item, a remove button each and an add button. fields: [{key, placeholder,
// type, flex}]; number fields are stored as numbers ("" while blank).
function RowListEditor({ rows, fields, onChange, addLabel }) {
  const list = rows || [];
  const blank = Object.fromEntries(fields.map(f => [f.key, ""]));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {list.map((row, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {fields.map(f => (
            <input
              key={f.key}
              style={{ ...inputStyle, flex: f.flex || 1 }}
              type={f.type || "text"}
              value={row[f.key] ?? ""}
              placeholder={f.placeholder}
              onChange={e => {
                const v = f.type === "number" ? (e.target.value === "" ? "" : +e.target.value) : e.target.value;
                onChange(list.map((r, idx) => (idx === i ? { ...r, [f.key]: v } : r)));
              }}
            />
          ))}
          <button
            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            title="Remove"
            style={{ background: "none", border: "none", color: "#B42318", cursor: "pointer", fontSize: 14, padding: "0 4px" }}
          >
            ✕
          </button>
        </div>
      ))}
      <button
        onClick={() => onChange([...list, blank])}
        style={{ alignSelf: "flex-start", padding: "5px 12px", borderRadius: 8, border: `1px dashed ${C.border}`, background: "transparent", color: C.brand, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
      >
        {addLabel}
      </button>
    </div>
  );
}

function SupplierCard({ supplier, onSave, onDelete, saving, deleting }) {
  const [local, setLocal] = useState(supplier);
  // Ticking Primary on another card unticks this one when that card saves.
  useEffect(() => { setLocal(l => ({ ...l, primary: supplier.primary })); }, [supplier.primary]);
  const dirty = JSON.stringify(local) !== JSON.stringify(supplier);
  const set = (key, value) => setLocal(l => ({ ...l, [key]: value }));
  const staleDays = daysSince(local.priceLastUpdated);
  const isStale = staleDays != null && staleDays > SUPPLIER_PRICE_STALE_DAYS;

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, padding: 18, marginBottom: 14, background: local.active ? "#fff" : "#FAFBFC" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <input
          style={{ fontSize: 15, fontWeight: 700, color: C.text, border: "none", outline: "none", background: "transparent", flex: 1, minWidth: 0, fontFamily: "inherit" }}
          value={local.name}
          onChange={e => set("name", e.target.value)}
          placeholder="Supplier Name"
        />
        <label title="The calculators and new POs use the Primary dry ice supplier unless another is picked for that quote." style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMuted, whiteSpace: "nowrap", cursor: "pointer" }}>
          <input type="checkbox" checked={!!local.primary} onChange={e => set("primary", e.target.checked)} />
          Primary
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMuted, whiteSpace: "nowrap", cursor: "pointer" }}>
          <input type="checkbox" checked={local.active} onChange={e => set("active", e.target.checked)} />
          Active
        </label>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <LabeledInput label="Order Email Address" value={local.orderEmail} onChange={v => set("orderEmail", v)} placeholder="orders@supplier.com" />
        <LabeledInput label="Account / Customer Number" value={local.accountNumber} onChange={v => set("accountNumber", v)} />
        <LabeledInput label="Default Lead Time (days)" type="number" value={local.defaultLeadTimeDays} onChange={v => set("defaultLeadTimeDays", v)} />
        <LabeledInput label="Order Cutoff Time" value={local.orderCutoffTime} onChange={v => set("orderCutoffTime", v)} placeholder="11:00" />
        <LabeledInput label="Current Cost Price (£/kg)" type="number" value={local.currentCostPricePerKg} onChange={v => set("currentCostPricePerKg", v)} />
        <LabeledInput label="Price Last Updated" value={local.priceLastUpdated} readOnly />
        <LabeledInput label="Bin/Container Rental (£/bin)" type="number" value={local.binRentalPerBin} onChange={v => set("binRentalPerBin", v)} />
        <LabeledInput label="Minimum Order Quantity (kg)" type="number" value={local.minimumOrderKg} onChange={v => set("minimumOrderKg", v)} />
        <LabeledInput label="Payment Terms" value={local.paymentTerms} onChange={v => set("paymentTerms", v)} />
        <LabeledInput label="Yard Delivery Address" value={local.yardDeliveryAddress} onChange={v => set("yardDeliveryAddress", v)} />
      </div>

      <div style={{ marginTop: 12 }}>
        <LabeledInput
          label="Delivery Surcharge (£/bin) — Yard/TR27 zone rate, confirm if delivering elsewhere"
          type="number"
          value={local.deliverySurchargePerBin}
          onChange={v => set("deliverySurchargePerBin", v)}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={labelStyle}>Bin Capacities</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(local.binCapacities || []).map((bin, i) => (
            <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                style={{ ...inputStyle, flex: 2 }}
                value={bin.label}
                placeholder="Label (e.g. 300-series)"
                onChange={e => {
                  const next = local.binCapacities.map((b, idx) => (idx === i ? { ...b, label: e.target.value } : b));
                  set("binCapacities", next);
                }}
              />
              <input
                style={{ ...inputStyle, flex: 1 }}
                type="number"
                value={bin.capacity_kg}
                placeholder="kg"
                onChange={e => {
                  const next = local.binCapacities.map((b, idx) => (idx === i ? { ...b, capacity_kg: e.target.value === "" ? "" : +e.target.value } : b));
                  set("binCapacities", next);
                }}
              />
              <button
                onClick={() => set("binCapacities", local.binCapacities.filter((_, idx) => idx !== i))}
                title="Remove"
                style={{ background: "none", border: "none", color: "#B42318", cursor: "pointer", fontSize: 14, padding: "0 4px" }}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            onClick={() => set("binCapacities", [...(local.binCapacities || []), { label: "", capacity_kg: "" }])}
            style={{ alignSelf: "flex-start", padding: "5px 12px", borderRadius: 8, border: `1px dashed ${C.border}`, background: "transparent", color: C.brand, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
          >
            + Add Bin Size
          </button>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={labelStyle}>Delivered Price Tiers (optional, £ ex VAT, delivery and collection included)</label>
        <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>
          When set, the calculators and POs price dry ice from these tiers (the cheapest tier or combination covering the job's kg) instead of £/kg, bins and delivery surcharge.
        </div>
        <RowListEditor
          rows={local.deliveredTiers}
          fields={[{ key: "kg", placeholder: "kg", type: "number" }, { key: "price", placeholder: "£ ex VAT", type: "number" }]}
          onChange={v => set("deliveredTiers", v)}
          addLabel="+ Add Tier"
        />
        {(local.deliveredTiers || []).length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
            <div>
              <label style={labelStyle}>Tiers Valid To</label>
              <input style={inputStyle} type="date" value={local.tiersValidTo || ""} onChange={e => set("tiersValidTo", e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {(local.deliveredTiers || []).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <label style={labelStyle}>Delivery Extras (optional, ticked per PO)</label>
          <RowListEditor
            rows={local.deliveredExtras}
            fields={[{ key: "label", placeholder: "e.g. Saturday delivery", flex: 2 }, { key: "price", placeholder: "£ ex VAT", type: "number" }]}
            onChange={v => set("deliveredExtras", v)}
            addLabel="+ Add Extra"
          />
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <label style={labelStyle}>Products Supplied</label>
        <textarea style={{ ...inputStyle, minHeight: 56, resize: "vertical" }} value={local.productsSupplied} onChange={e => set("productsSupplied", e.target.value)} />
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={labelStyle}>Notes</label>
        <textarea style={{ ...inputStyle, minHeight: 44, resize: "vertical" }} value={local.notes} onChange={e => set("notes", e.target.value)} />
      </div>

      {isStale && (
        <div style={{ marginTop: 12, padding: "8px 12px", background: "#FEF3E1", border: "1px solid #F5D28C", borderRadius: 8, fontSize: 12, color: "#93630A" }}>
          ⚠ Price last checked {staleDays} days ago (over {SUPPLIER_PRICE_STALE_DAYS}) — worth confirming it's still current.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button
          onClick={() => onSave(local)}
          disabled={saving || !dirty}
          style={{
            padding: "9px 16px", borderRadius: 8, border: "none",
            background: !dirty ? "#EEF1F9" : saving ? "#8fa3c4" : C.brand, color: !dirty ? "#9aa3b5" : "#fff",
            fontSize: 12, fontWeight: 700, cursor: (saving || !dirty) ? "default" : "pointer",
          }}
        >
          {saving ? "Saving…" : dirty ? "Save Supplier" : "Saved"}
        </button>
        <button
          onClick={() => onDelete(local.id)}
          disabled={deleting}
          style={{
            padding: "9px 16px", borderRadius: 8, border: "none",
            background: "#FDECEB", color: "#A16A66",
            fontSize: 12, fontWeight: 700, cursor: deleting ? "default" : "pointer", opacity: deleting ? 0.6 : 1,
          }}
        >
          {deleting ? "Removing…" : "Delete"}
        </button>
      </div>
    </div>
  );
}

function SectionCard({ title, children, action }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: 22, marginBottom: 20, boxShadow: "0 8px 24px rgba(20,30,70,0.04)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</div>
        {action}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {children}
      </div>
    </div>
  );
}

export default function CompanySettingsPage() {
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.role === "admin";

  const [pageTab, setPageTab] = useState("settings"); // "settings" | "suppliers"
  const [settings, setSettings] = useState({});
  const [documents, setDocuments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [isMobile] = useState(() => window.innerWidth < 768);

  const [newDocCategory, setNewDocCategory] = useState("");
  const [newDocDescription, setNewDocDescription] = useState("");
  const [newDocUrl, setNewDocUrl] = useState("");
  const [addingDoc, setAddingDoc] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const [savingSupplierId, setSavingSupplierId] = useState(null);
  const [deletingSupplierId, setDeletingSupplierId] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([fetchCompanySettings(), fetchDocuments(), fetchSuppliers()])
      .then(([s, docs, sups]) => { setSettings(s); setDocuments(docs); setSuppliers(sups); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  // Defense in depth: the menu entry that links here is already hidden from
  // non-admins (see App.jsx), but this page checks for itself too, in case
  // it's ever reached another way.
  if (!isAdmin) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: 32, maxWidth: 360, textAlign: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>Access restricted</div>
          <div style={{ fontSize: 13, color: C.textMuted }}>Admin is only available to admin accounts.</div>
        </div>
      </div>
    );
  }

  function handleChange(key, value) {
    setSettings(s => ({ ...s, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await saveCompanySettings(settings);
      setSuccessMsg("Saved");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleCopyBank() {
    const text = [
      `Account Name: ${settings.bankAccountName || "—"}`,
      `Sort Code: ${settings.bankSortCode || "—"}`,
      `Account Number: ${settings.bankAccountNumber || "—"}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      alert("Couldn't copy to clipboard: " + e.message);
    }
  }

  // Documents save immediately on add/delete rather than waiting for the
  // main "Save Changes" button below - they're a separate sheet/dataset
  // from the settings fields, and an "added but not actually saved until
  // you also click a button elsewhere" list would be confusing.
  async function handleAddDocument() {
    if (!newDocCategory.trim() || !newDocDescription.trim() || !newDocUrl.trim()) {
      alert("Category, Description, and Drive URL are all required.");
      return;
    }
    setAddingDoc(true);
    try {
      const updated = await addDocument({ category: newDocCategory, description: newDocDescription, url: newDocUrl });
      setDocuments(updated);
      setNewDocCategory("");
      setNewDocDescription("");
      setNewDocUrl("");
    } catch (e) {
      alert("Failed to add document: " + e.message);
    } finally {
      setAddingDoc(false);
    }
  }

  async function handleDeleteDocument(id) {
    if (!window.confirm("Remove this document?")) return;
    setDeletingDocId(id);
    try {
      const updated = await deleteDocument(id);
      setDocuments(updated);
    } catch (e) {
      alert("Failed to remove document: " + e.message);
    } finally {
      setDeletingDocId(null);
    }
  }

  // Unlike Documents, each supplier is a much larger record that gets
  // edited in place - saved individually (per-card Save button) so editing
  // one supplier can't stomp on another's unsaved changes, and so "Add
  // Supplier" doesn't write anything until its fields are actually filled
  // in and saved.
  function handleAddSupplier() {
    setSuppliers(s => [...s, {
      id: `sup-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      name: "", orderEmail: "", accountNumber: "", productsSupplied: "",
      defaultLeadTimeDays: "", orderCutoffTime: "", currentCostPricePerKg: "",
      priceLastUpdated: "", binRentalPerBin: "", deliverySurchargePerBin: "",
      minimumOrderKg: "", binCapacities: [],
      paymentTerms: "", yardDeliveryAddress: settings.tradingAddress || "",
      active: true, notes: "",
      primary: false, deliveredTiers: [], deliveredExtras: [], tiersValidTo: "",
    }]);
  }

  async function handleSaveSupplier(supplier) {
    setSavingSupplierId(supplier.id);
    try {
      const updated = await saveSupplier(supplier);
      setSuppliers(updated);
    } catch (e) {
      alert("Failed to save supplier: " + e.message);
    } finally {
      setSavingSupplierId(null);
    }
  }

  async function handleDeleteSupplier(id) {
    if (!window.confirm("Remove this supplier?")) return;
    setDeletingSupplierId(id);
    try {
      // A never-saved new card (Add Supplier, not yet clicked Save) only
      // exists in local state - deleting it shouldn't call the API at all.
      const serverList = await fetchSuppliers();
      const wasEverSaved = serverList.some(s => s.id === id);
      const updated = wasEverSaved ? await deleteSupplier(id) : suppliers.filter(s => s.id !== id);
      setSuppliers(updated);
    } catch (e) {
      alert("Failed to remove supplier: " + e.message);
    } finally {
      setDeletingSupplierId(null);
    }
  }

  const fieldsByKey = Object.fromEntries(COMPANY_SETTINGS_FIELDS.map(f => [f.key, f]));
  const bankFields = COMPANY_SETTINGS_FIELDS.filter(f => f.group === "bank");
  const otherFields = COMPANY_SETTINGS_FIELDS.filter(f => f.group !== "bank");
  const companyFields = ["companyName", "tradingAs", "registeredAddress", "tradingAddress", "companyNumber", "vatNumber", "companyEmail", "phoneNumber", "websiteUrl"].map(k => fieldsByKey[k]);
  const termsFields = ["quoteValidityDays", "paymentTerms", "defaultVatRate"].map(k => fieldsByKey[k]);
  const resourceFields = ["appServiceStackUrl", "latestHandoverDocUrl"].map(k => fieldsByKey[k]);
  const docCategoryOptions = [...new Set(documents.map(d => d.category).filter(Boolean))].sort();

  return (
    <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div style={{ padding: isMobile ? "20px 16px 40px" : "32px 40px 56px", maxWidth: 760, margin: "0 auto" }}>
        <div style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, color: C.text, letterSpacing: "-0.02em", marginBottom: 4 }}>
          Admin
        </div>
        <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 20 }}>
          Business reference data used across quotes and contracts. No API keys or credentials live here — those are managed in Vercel.
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          {[{ key: "settings", label: "Settings" }, { key: "suppliers", label: "Suppliers" }].map(tb => (
            <button
              key={tb.key}
              onClick={() => setPageTab(tb.key)}
              style={{
                padding: "9px 18px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontWeight: 700,
                border: pageTab === tb.key ? "none" : `1px solid ${C.border}`,
                background: pageTab === tb.key ? C.brand : C.surface,
                color: pageTab === tb.key ? "#fff" : C.textMuted,
              }}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {successMsg && (
          <div style={{ padding: "10px 16px", background: "#E6F9F0", color: "#0D5C36", border: "1px solid #27AE60", borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 500 }}>
            ✓ {successMsg}
          </div>
        )}
        {error && (
          <div style={{ padding: "12px 16px", background: "#FEECEC", color: "#8B1A1A", border: "1px solid #E53935", borderRadius: 10, marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: 40, color: C.textMuted }}>Loading settings…</div>
        ) : pageTab === "suppliers" ? (
          <>
            {suppliers.map(supplier => (
              <SupplierCard
                key={supplier.id}
                supplier={supplier}
                onSave={handleSaveSupplier}
                onDelete={handleDeleteSupplier}
                saving={savingSupplierId === supplier.id}
                deleting={deletingSupplierId === supplier.id}
              />
            ))}
            <button
              onClick={handleAddSupplier}
              style={{
                width: "100%", padding: 13, borderRadius: 12, border: `1px dashed ${C.border}`,
                background: "transparent", color: C.brand,
                fontSize: 14, fontWeight: 700, cursor: "pointer",
              }}
            >
              + Add Supplier
            </button>
          </>
        ) : (
          <>
            <SectionCard title="Company Details">
              {companyFields.map(f => (
                <Field key={f.key} field={f} value={settings[f.key] || ""} onChange={handleChange} />
              ))}
            </SectionCard>

            <SectionCard
              title="Bank Details"
              action={
                <button
                  onClick={handleCopyBank}
                  style={{
                    padding: "6px 14px", borderRadius: 8, border: "none",
                    background: copied ? "#E6F9F0" : "#EEF1F9", color: copied ? "#0D5C36" : "#4A5A85",
                    fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {copied ? "✓ Copied" : "Copy"}
                </button>
              }
            >
              {bankFields.map(f => (
                <Field key={f.key} field={f} value={settings[f.key] || ""} onChange={handleChange} />
              ))}
            </SectionCard>

            <SectionCard title="Documents">
              {documents.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {documents.map(doc => (
                    <div key={doc.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "9px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#4A5A85", background: "#EEF1F9", padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap", flexShrink: 0 }}>
                          {doc.category}
                        </span>
                        <span style={{ fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {doc.description}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                        <a href={doc.url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: C.brand, fontWeight: 600, textDecoration: "none" }}>
                          Open ↗
                        </a>
                        <button
                          onClick={() => handleDeleteDocument(doc.id)}
                          disabled={deletingDocId === doc.id}
                          title="Remove"
                          style={{ background: "none", border: "none", color: "#B42318", fontSize: 14, lineHeight: 1, padding: 2, cursor: deletingDocId === doc.id ? "default" : "pointer", opacity: deletingDocId === doc.id ? 0.5 : 1 }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: documents.length > 0 ? 14 : 0, paddingTop: documents.length > 0 ? 14 : 0, borderTop: documents.length > 0 ? `1px solid ${C.border}` : "none" }}>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr 2fr auto", gap: 8, alignItems: "start" }}>
                  <div>
                    <input
                      style={inputStyle}
                      list="doc-category-options"
                      value={newDocCategory}
                      onChange={e => setNewDocCategory(e.target.value)}
                      placeholder="Category"
                    />
                    <datalist id="doc-category-options">
                      {docCategoryOptions.map(c => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                  <input
                    style={inputStyle}
                    value={newDocDescription}
                    onChange={e => setNewDocDescription(e.target.value)}
                    placeholder="Description (e.g. Public Liability Cover Note 2026)"
                  />
                  <input
                    style={inputStyle}
                    type="url"
                    value={newDocUrl}
                    onChange={e => setNewDocUrl(e.target.value)}
                    placeholder="Drive URL"
                  />
                  <button
                    onClick={handleAddDocument}
                    disabled={addingDoc}
                    style={{
                      padding: "10px 16px", borderRadius: 10, border: "none",
                      background: addingDoc ? "#8fa3c4" : C.brand, color: "#fff",
                      fontSize: 13, fontWeight: 700, cursor: addingDoc ? "default" : "pointer", whiteSpace: "nowrap",
                    }}
                  >
                    {addingDoc ? "Adding…" : "Add Document"}
                  </button>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Standard Terms">
              {termsFields.map(f => (
                <Field key={f.key} field={f} value={settings[f.key] || ""} onChange={handleChange} />
              ))}
            </SectionCard>

            <SectionCard title="Resources">
              {resourceFields.map(f => (
                <Field key={f.key} field={f} value={settings[f.key] || ""} onChange={handleChange} />
              ))}
            </SectionCard>

            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                width: "100%", padding: 13, borderRadius: 12, border: "none",
                background: saving ? "#8fa3c4" : C.brand, color: "#fff",
                fontSize: 14, fontWeight: 700, cursor: saving ? "default" : "pointer",
              }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
